import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner, Table, Row, Col, Card } from 'react-bootstrap';
import Wrapper from '../../components/Wrapper';
import { fetchAnomalyLogs, fetchAnomalyReasons, markLogFixed } from '../../store/features/soilAnomalyLogSlice';

const PARAM_LABELS = {
  ph: 'pH', ec: 'EC', oc: 'OC', nitrogen: 'Available Nitrogen',
  phosphorus: 'Phosphorus', potassium: 'Potassium', sulphur: 'Sulphur',
  magnesium: 'Magnesium', calcium: 'Calcium', zinc: 'Zinc',
  iron: 'Iron', copper: 'Copper', boron: 'Boron',
};

export default function SoilAnomalyLogs() {
  const dispatch = useDispatch();
  const { logs, total, totalPages, status, error, reasonsByLogId, reasonsStatus, reasonsError } = useSelector(
    (state) => state.soilAnomalyLogs
  );
  const supportUser = useSelector((state) => state.support?.support);
  const loggedInIdentifier =
    supportUser?.user_id ||
    supportUser?.admin_id ||
    supportUser?.email ||
    supportUser?.name ||
    '';

  const [filterStatus, setFilterStatus] = useState('all');
  const [sampleSearch, setSampleSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedLogIds, setSelectedLogIds] = useState([]);

  const [showFixModal, setShowFixModal] = useState(false);
  const [fixingLogs, setFixingLogs]     = useState([]);
  const [fixReason, setFixReason]       = useState('');
  const [fixLoading, setFixLoading]     = useState(false);
  const [fixError, setFixError]         = useState('');

  // View modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingLog, setViewingLog]       = useState(null);

  const load = useCallback(
    (pg = 1) => {
      dispatch(
        fetchAnomalyLogs({
          page: pg,
          limit: 20,
          status: filterStatus,
          sample_id: sampleSearch.trim(),
        })
      );
      setCurrentPage(pg);
      setSelectedLogIds([]);
    },
    [dispatch, filterStatus, sampleSearch]
  );

  useEffect(() => { load(1); }, [load]);

  const handleSearch = (e) => {
    if (e.type === 'click' || e.key === 'Enter') load(1);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const openLogs = logs.filter(l => !l.is_fixed).map(l => l.id);
      setSelectedLogIds(openLogs);
    } else {
      setSelectedLogIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedLogIds(prev => 
      prev.includes(id) ? prev.filter(logId => logId !== id) : [...prev, id]
    );
  };

  const openFixModal = (logsToFix) => {
    setFixingLogs(logsToFix);
    setFixReason('');
    setFixError('');
    setShowFixModal(true);
    if (logsToFix.length === 1) {
      dispatch(fetchAnomalyReasons(logsToFix[0].id));
    }
  };

  const openViewModal = (log) => {
    setViewingLog(log);
    setShowViewModal(true);
  };

  const submitFix = async () => {
    if (!loggedInIdentifier) { setFixError('Could not detect logged-in user id/name. Please re-login.'); return; }
    if (!fixReason.trim()) { setFixError('Please enter the root-cause reason.'); return; }
    setFixLoading(true);
    setFixError('');
    try {
      await dispatch(markLogFixed({
        ids: fixingLogs.map(l => l.id),
        fixed_by: loggedInIdentifier,
        fixed_reason: fixReason.trim(),
      })).unwrap();
      
      load(currentPage);
      setShowFixModal(false);
      setSelectedLogIds([]);
    } catch (err) {
      setFixError(err || 'Failed to mark as fixed.');
    } finally {
      setFixLoading(false);
    }
  };

  const formatDate = (val) => val ? new Date(val).toLocaleString('en-IN') : '—';
  const formatValue = (val) => {
    if (val === null || val === undefined || val === '') return '-';
    return String(val);
  };

  const openLogsCount = logs.filter(l => !l.is_fixed).length;
  const isAllSelected = openLogsCount > 0 && selectedLogIds.length === openLogsCount;

  return (
    <Wrapper>
      <div className="container-fluid py-3">
        <h4 className="mb-3 fw-semibold">Soil Parameter Anomaly Logs</h4>

        {/* Filters & Batch Action */}
        <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
          <Form.Select
            style={{ width: 160 }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="fixed">Fixed</option>
          </Form.Select>

          <InputGroup style={{ width: 280 }}>
            <Form.Control
              placeholder="Search by Sample ID"
              value={sampleSearch}
              onChange={(e) => setSampleSearch(e.target.value)}
              onKeyDown={handleSearch}
            />
            <Button variant="outline-secondary" onClick={handleSearch}>Search</Button>
          </InputGroup>

          {selectedLogIds.length > 0 && (
            <Button 
              variant="success" 
              onClick={() => openFixModal(logs.filter(l => selectedLogIds.includes(l.id)))}
            >
              Mark Selected Fixed ({selectedLogIds.length})
            </Button>
          )}

          <span className="text-muted small ms-auto">
            {status === 'succeeded' ? `${total} record(s) found` : ''}
          </span>
        </div>

        {/* States */}
        {status === 'loading' && (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        )}
        {status === 'failed' && (
          <Alert variant="danger">{error || 'Failed to load anomaly logs.'}</Alert>
        )}

        {/* Table */}
        {status === 'succeeded' && (
          <>
            {logs.length === 0 ? (
              <Alert variant="info">No anomaly logs found.</Alert>
            ) : (
              <div className="table-responsive">
                <Table bordered hover size="sm" className="align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <Form.Check 
                          type="checkbox" 
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          disabled={openLogsCount === 0}
                        />
                      </th>
                      <th>#</th>
                      <th>Sample ID</th>
                      <th>Report ID</th>
                      <th>Parameter</th>
                      <th>Original Value</th>
                      <th>Capped To</th>
                      <th>Logged At</th>
                      <th>Status</th>
                      <th style={{ minWidth: 140 }}>Updated At</th>
                      <th style={{ textAlign: 'center', minWidth: 180 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, rowIndex) => (
                      <tr
                        key={log.id}
                        style={{ background: log.is_fixed ? '#f0fff4' : '#fff8f8' }}
                      >
                        <td style={{ textAlign: 'center' }}>
                          {!log.is_fixed && (
                            <Form.Check 
                              type="checkbox" 
                              checked={selectedLogIds.includes(log.id)}
                              onChange={() => handleToggleSelect(log.id)}
                            />
                          )}
                        </td>
                        <td>{(currentPage - 1) * 20 + rowIndex + 1}</td>
                        <td><code>{log.sample_id}</code></td>
                        <td>{log.report_id}</td>
                        <td><strong>{PARAM_LABELS[log.parameter] || log.parameter}</strong></td>
                        <td>{formatValue(log.original_value)}</td>
                        <td>{formatValue(log.capped_value)}</td>
                        <td>{formatDate(log.created_at)}</td>
                        <td>
                          {log.is_fixed ? (
                            <Badge bg="success">Fixed</Badge>
                          ) : (
                            <Badge bg="danger">Open</Badge>
                          )}
                        </td>
                        <td>{formatDate(log.updated_at)}</td>
                        <td>
                          <div className="d-flex gap-2 justify-content-center">
                            <Button
                              size="sm"
                              variant="outline-info"
                              onClick={() => openViewModal(log)}
                            >
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant={log.is_fixed ? "outline-success" : "success"}
                              onClick={() => openFixModal([log])}
                            >
                              {log.is_fixed ? 'Add Reason' : 'Fixed'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex gap-2 justify-content-center mt-3">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={currentPage <= 1}
                  onClick={() => load(currentPage - 1)}
                >
                  Prev
                </Button>
                <span className="align-self-center small">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() => load(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Details Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Report Details</Modal.Title></Modal.Header>
        <Modal.Body>
          {viewingLog && (
            <div>
              <div className="mb-4">
                <h6 className="fw-bold text-secondary border-bottom pb-2">Anomaly Info</h6>
                <Row>
                  <Col md={6}><p className="mb-1"><strong>Report ID:</strong> {viewingLog.report_id}</p></Col>
                  <Col md={6}><p className="mb-1"><strong>Sample ID:</strong> {viewingLog.sample_id}</p></Col>
                  <Col md={6}><p className="mb-1"><strong>Parameter:</strong> {PARAM_LABELS[viewingLog.parameter] || viewingLog.parameter}</p></Col>
                  <Col md={6}><p className="mb-1"><strong>Status:</strong> {viewingLog.is_fixed ? 'Fixed' : 'Open'}</p></Col>
                </Row>
              </div>

              <Row className="g-3">
                <Col md={4}>
                  <Card className="h-100 shadow-sm border-0 bg-light">
                    <Card.Body>
                      <Card.Title className="h6 fw-bold text-primary mb-3">User (Farmer)</Card.Title>
                      <p className="mb-1 small"><strong>Name:</strong> {viewingLog.user_first_name} {viewingLog.user_last_name}</p>
                      <p className="mb-1 small"><strong>Mobile:</strong> {viewingLog.user_mobile || 'N/A'}</p>
                      <p className="mb-1 small"><strong>Village:</strong> {viewingLog.user_village || 'N/A'}</p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="h-100 shadow-sm border-0 bg-light">
                    <Card.Body>
                      <Card.Title className="h6 fw-bold text-success mb-3">Field Officer</Card.Title>
                      <p className="mb-1 small"><strong>Name:</strong> {viewingLog.fo_name || 'N/A'}</p>
                      <p className="mb-1 small"><strong>Mobile:</strong> {viewingLog.fo_mobile || 'N/A'}</p>
                      <p className="mb-1 small"><strong>Entity:</strong> {viewingLog.fo_entity || 'N/A'}</p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="h-100 shadow-sm border-0 bg-light">
                    <Card.Body>
                      <Card.Title className="h6 fw-bold text-danger mb-3">Admin / Partner</Card.Title>
                      <p className="mb-1 small"><strong>Entity:</strong> {viewingLog.admin_entity || 'N/A'}</p>
                      <p className="mb-1 small"><strong>Contact:</strong> {viewingLog.admin_name || 'N/A'}</p>
                      <p className="mb-1 small"><strong>Mobile:</strong> {viewingLog.admin_mobile || 'N/A'}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Fix Modal */}
      <Modal show={showFixModal} onHide={() => setShowFixModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{fixingLogs.length > 1 ? 'Batch Mark as Fixed' : 'Mark as Fixed'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Selected Anomalies to Fix:</strong>
            <ul className="mb-0 mt-1 text-muted small ps-3" style={{ maxHeight: '100px', overflowY: 'auto' }}>
              {fixingLogs.map(log => (
                <li key={log.id}>
                  Report <strong>#{log.report_id}</strong> ({log.sample_id}) — <strong>{PARAM_LABELS[log.parameter] || log.parameter}</strong>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="mb-2 small text-muted">
            Logged in as: <strong>{loggedInIdentifier || 'Unknown user'}</strong>
          </div>

          {fixingLogs.length === 1 && (
            <div className="border rounded p-2 mb-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
              <div className="fw-semibold mb-1">Existing reasons</div>
              {reasonsStatus === 'loading' && <div className="small text-muted">Loading...</div>}
              {reasonsStatus !== 'loading' && (reasonsByLogId[fixingLogs[0]?.id] || []).length === 0 && (
                <div className="small text-muted">No reasons added yet.</div>
              )}
              {(reasonsByLogId[fixingLogs[0]?.id] || []).map((r) => (
                <div key={r.id} className="small border-top pt-1 mt-1">
                  <div><strong>{r.fixed_by}</strong> · {formatDate(r.created_at)}</div>
                  <div>{r.reason_text}</div>
                </div>
              ))}
              {reasonsError && <div className="small text-danger mt-1">{reasonsError}</div>}
            </div>
          )}

          <Form.Group className="mt-2">
            <Form.Label>Root-cause reason <span className="text-danger">*</span></Form.Label>
            <Form.Control
              autoFocus
              as="textarea"
              rows={3}
              placeholder="Why did these anomalies happen, and what was fixed?"
              value={fixReason}
              onChange={(e) => setFixReason(e.target.value)}
            />
          </Form.Group>
          {fixError && <Alert variant="danger" className="mt-2 mb-0 py-1 small">{fixError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFixModal(false)} disabled={fixLoading}>
            Cancel
          </Button>
          <Button variant="success" onClick={submitFix} disabled={fixLoading}>
            {fixLoading ? <Spinner animation="border" size="sm" /> : `Confirm Fixed (${fixingLogs.length})`}
          </Button>
        </Modal.Footer>
      </Modal>
    </Wrapper>
  );
}